import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Grid,
    TextField,
    Button,
    Typography,
    CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import api from '../../../../../config/axiosConfig';
import PersianDatePicker from '../../../../common/PersianDatePicker';
import { toPersianDigits, formatNumberInputValue, parseNumberInput } from '../../../../../utils/convertNumbers';
import { API } from '../../../../../utils/apiRoutes';

function CashboxReconciliationForm({ reconciliation, onSuccess, onCancel }) {
    const theme = useTheme();
    const [formData, setFormData] = useState({
        shift_date: '',
        opening_balance: '',
        closing_balance: '',
        expected_total: '',
        notes: '',
        supporting_files: [],
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [fileInputs, setFileInputs] = useState([]);

    useEffect(() => {
        if (reconciliation) {
            // Edit mode - populate form with existing data
            setFormData({
                shift_date: reconciliation.shift_date || '',
                opening_balance: reconciliation.opening_balance || '',
                closing_balance: reconciliation.closing_balance || '',
                expected_total: reconciliation.expected_total || '',
                notes: reconciliation.notes || '',
                supporting_files: [],
            });
            // Store existing files for display
            const existingFiles = reconciliation.supporting_files || [];
            setFileInputs(existingFiles.map((file, index) => ({ id: `existing-${index}`, url: file, isExisting: true })));
        } else {
            // Create mode - reset form
            setFormData({
                shift_date: '',
                opening_balance: '',
                closing_balance: '',
                expected_total: '',
                notes: '',
                supporting_files: [],
            });
            setFileInputs([]);
        }
    }, [reconciliation]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        // Clear error for this field
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    const handleFileChange = (e, index) => {
        const files = Array.from(e.target.files || []);
        const newFileInputs = [...fileInputs];
        
        files.forEach((file) => {
            newFileInputs.push({
                id: `new-${Date.now()}-${Math.random()}`,
                file: file,
                isExisting: false
            });
        });
        
        setFileInputs(newFileInputs);
    };

    const handleRemoveFile = (fileId) => {
        setFileInputs(prev => prev.filter(f => f.id !== fileId));
    };

    const validate = () => {
        const newErrors = {};
        
        if (!formData.shift_date) {
            newErrors.shift_date = 'لطفاً تاریخ شیفت را انتخاب کنید';
        }
        
        if (formData.opening_balance === '' || formData.opening_balance < 0) {
            newErrors.opening_balance = 'لطفاً موجودی ابتدای شیفت را وارد کنید (حداقل ۰)';
        }
        
        if (formData.closing_balance === '' || formData.closing_balance < 0) {
            newErrors.closing_balance = 'لطفاً موجودی انتهای شیفت را وارد کنید (حداقل ۰)';
        }
        
        if (formData.expected_total === '' || formData.expected_total < 0) {
            newErrors.expected_total = 'لطفاً مبلغ انتظاری را وارد کنید (حداقل ۰)';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validate()) {
            return;
        }

        setIsSubmitting(true);

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('shift_date', formData.shift_date);
            formDataToSend.append('opening_balance', formData.opening_balance || 0);
            formDataToSend.append('closing_balance', formData.closing_balance);
            formDataToSend.append('expected_total', formData.expected_total);
            if (formData.notes) {
                formDataToSend.append('notes', formData.notes);
            }
            
            // Handle supporting files
            const newFiles = fileInputs.filter(f => !f.isExisting).map(f => f.file);
            // For existing files, we'll keep them by not sending anything (they should persist)
            // For new files, we need to handle them differently - JSON field might need special handling
            // Since supporting_files is a JSONField, we might need to send existing files as JSON
            // But let's start by sending new files if any
            // Note: The backend might need to handle file uploads differently for JSONField
            // For now, we'll keep the existing files and add note that file upload needs backend adjustment
            
            if (reconciliation) {
                // Update existing reconciliation
                // Keep existing files from reconciliation.supporting_files
                const existingFiles = reconciliation.supporting_files || [];
                const allFiles = [...existingFiles, ...newFiles.map(f => f.name)];
                formDataToSend.append('supporting_files', JSON.stringify(allFiles));
                
                await api.patch(`${API.cashboxReconciliations}${reconciliation.id}/`, formDataToSend, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });
            } else {
                // Create new reconciliation
                if (newFiles.length > 0) {
                    formDataToSend.append('supporting_files', JSON.stringify(newFiles.map(f => f.name)));
                }
                
                await api.post(API.cashboxReconciliations, formDataToSend, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });
            }

            onSuccess();
        } catch (error) {
            console.error('Error saving cashbox reconciliation:', error);
            if (error.response?.data) {
                // Set field-specific errors if available
                const fieldErrors = {};
                Object.keys(error.response.data).forEach(key => {
                    if (Array.isArray(error.response.data[key])) {
                        fieldErrors[key] = error.response.data[key][0];
                    } else {
                        fieldErrors[key] = error.response.data[key];
                    }
                });
                setErrors(fieldErrors);
            } else {
                setErrors({ submit: 'خطا در ذخیره تطبیق صندوق. لطفاً دوباره تلاش کنید.' });
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const formatCurrency = (amount) => {
        if (!amount && amount !== 0) return '';
        return toPersianDigits(new Intl.NumberFormat('fa-IR').format(amount));
    };

    // Calculate difference for display
    const calculatedDifference = useMemo(() => {
        const closing = parseInt(formData.closing_balance) || 0;
        const expected = parseInt(formData.expected_total) || 0;
        return closing - expected;
    }, [formData.closing_balance, formData.expected_total]);

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                    <PersianDatePicker
                        label="تاریخ شیفت"
                        value={formData.shift_date ? new Date(formData.shift_date) : null}
                        onChange={(date) => {
                            if (date && !isNaN(date.getTime())) {
                                handleChange('shift_date', date.toISOString().split('T')[0]);
                            } else {
                                handleChange('shift_date', '');
                            }
                        }}
                        error={!!errors.shift_date}
                        helperText={errors.shift_date}
                        required
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label="موجودی ابتدای شیفت (تومان)"
                        type="text"
                        inputMode="numeric"
                        value={formatNumberInputValue(formData.opening_balance)}
                        onChange={(e) => {
                            const parsed = parseNumberInput(e.target.value, false); // false = integer only
                            handleChange('opening_balance', parsed);
                        }}
                        error={!!errors.opening_balance}
                        helperText={errors.opening_balance}
                        required
                        inputProps={{ min: 0 }}
                    />
                    {formData.opening_balance !== '' && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            {formatCurrency(formData.opening_balance)} تومان
                        </Typography>
                    )}
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label="مبلغ انتظاری (تومان)"
                        type="text"
                        inputMode="numeric"
                        value={formatNumberInputValue(formData.expected_total)}
                        onChange={(e) => {
                            const parsed = parseNumberInput(e.target.value, false); // false = integer only
                            handleChange('expected_total', parsed);
                        }}
                        error={!!errors.expected_total}
                        helperText={errors.expected_total}
                        required
                        inputProps={{ min: 0 }}
                    />
                    {formData.expected_total !== '' && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            {formatCurrency(formData.expected_total)} تومان
                        </Typography>
                    )}
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label="موجودی انتهای شیفت (تومان)"
                        type="text"
                        inputMode="numeric"
                        value={formatNumberInputValue(formData.closing_balance)}
                        onChange={(e) => {
                            const parsed = parseNumberInput(e.target.value, false); // false = integer only
                            handleChange('closing_balance', parsed);
                        }}
                        error={!!errors.closing_balance}
                        helperText={errors.closing_balance}
                        required
                        inputProps={{ min: 0 }}
                    />
                    {formData.closing_balance !== '' && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            {formatCurrency(formData.closing_balance)} تومان
                        </Typography>
                    )}
                </Grid>

                {/* Calculated Difference Display */}
                {(formData.closing_balance !== '' && formData.expected_total !== '') && (
                    <Grid item xs={12}>
                        <Box sx={{ 
                            p: 2, 
                            bgcolor: calculatedDifference === 0 ? 'success.light' : (calculatedDifference > 0 ? 'info.light' : 'error.light'),
                            borderRadius: 1,
                            border: 1,
                            borderColor: calculatedDifference === 0 ? 'success.main' : (calculatedDifference > 0 ? 'info.main' : 'error.main')
                        }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                تفاوت محاسبه شده:
                            </Typography>
                            <Typography 
                                variant="h6" 
                                sx={{ 
                                    fontWeight: 'bold',
                                    color: calculatedDifference === 0 ? 'success.main' : (calculatedDifference > 0 ? 'info.main' : 'error.main')
                                }}
                            >
                                {calculatedDifference > 0 ? '+' : ''}{formatCurrency(calculatedDifference)} تومان
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                {calculatedDifference === 0 
                                    ? 'موجودی با مبلغ انتظاری مطابقت دارد' 
                                    : calculatedDifference > 0 
                                        ? 'موجودی بیشتر از انتظار است'
                                        : 'موجودی کمتر از انتظار است'
                                }
                            </Typography>
                        </Box>
                    </Grid>
                )}

                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="یادداشت"
                        multiline
                        rows={4}
                        value={formData.notes}
                        onChange={(e) => handleChange('notes', e.target.value)}
                        error={!!errors.notes}
                        helperText={errors.notes}
                        placeholder="توضیحات مربوط به تطبیق صندوق..."
                    />
                </Grid>

                <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        فایل‌های پشتیبان
                    </Typography>
                    {fileInputs.map((fileInput) => (
                        <Box key={fileInput.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography variant="body2" sx={{ flex: 1 }}>
                                {fileInput.isExisting ? (
                                    <a href={fileInput.url} target="_blank" rel="noopener noreferrer">
                                        {fileInput.url.split('/').pop()}
                                    </a>
                                ) : (
                                    fileInput.file.name
                                )}
                            </Typography>
                            <Button
                                size="small"
                                color="error"
                                onClick={() => handleRemoveFile(fileInput.id)}
                            >
                                حذف
                            </Button>
                        </Box>
                    ))}
                    <TextField
                        fullWidth
                        type="file"
                        inputProps={{ 
                            multiple: true,
                            accept: 'image/*,.pdf,.doc,.docx'
                        }}
                        onChange={(e) => handleFileChange(e)}
                        helperText="می‌توانید چند فایل را انتخاب کنید"
                    />
                </Grid>

                {errors.submit && (
                    <Grid item xs={12}>
                        <Typography variant="body2" color="error">
                            {errors.submit}
                        </Typography>
                    </Grid>
                )}

                <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                        <Button
                            onClick={onCancel}
                            disabled={isSubmitting}
                            variant="outlined"
                        >
                            انصراف
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={isSubmitting}
                            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                        >
                            {isSubmitting ? 'در حال ذخیره...' : (reconciliation ? 'ویرایش' : 'ثبت')}
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
}

export default CashboxReconciliationForm;

